import React from "react";
import PropTypes from "prop-types";
import { Plus, Trash2 } from "lucide-react";

const SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "OneSize"];

const SizeChartSection = ({
  selectedSizes = [],
  onSizeChange,
  disabled = false,
  displaySizeTable = false,
  onDisplayToggle,
  sizeChartHeaders = [],
  sizeChartData = [],
  onSizeChartChange,
}) => {
  const handleSizeClick = (size) => {
    if (!disabled) {
      if (selectedSizes.includes(size)) {
        onSizeChange(selectedSizes.filter((s) => s !== size));
      } else {
        onSizeChange([...selectedSizes, size]);
      }
    }
  };

  // ── Table helpers ──────────────────────────────────────────────────────────

  const emitChange = (headers, data) => {
    onSizeChartChange && onSizeChartChange({ headers, data });
  };

  const handleHeaderChange = (colIdx, val) => {
    const newHeaders = [...sizeChartHeaders];
    newHeaders[colIdx] = val;
    emitChange(newHeaders, sizeChartData);
  };

  const handleAddColumn = () => {
    const newHeaders = [...sizeChartHeaders, "New Column"];
    const newData = sizeChartData.map((row) => [...row, ""]);
    emitChange(newHeaders, newData);
  };

  const handleRemoveColumn = (colIdx) => {
    const newHeaders = sizeChartHeaders.filter((_, i) => i !== colIdx);
    const newData = sizeChartData.map((row) => row.filter((_, i) => i !== colIdx));
    emitChange(newHeaders, newData);
  };

  const handleCellChange = (rowIdx, colIdx, val) => {
    const newData = sizeChartData.map((row, rI) =>
      rI === rowIdx ? row.map((cell, cI) => (cI === colIdx ? val : cell)) : row
    );
    emitChange(sizeChartHeaders, newData);
  };

  const handleAddRow = () => {
    const newRow = Array(sizeChartHeaders.length).fill("");
    emitChange(sizeChartHeaders, [...sizeChartData, newRow]);
  };

  const handleRemoveRow = (rowIdx) => {
    emitChange(sizeChartHeaders, sizeChartData.filter((_, i) => i !== rowIdx));
  };

  const handleInitTable = () => {
    const defaultHeaders = ["Size", "Chest (inches)", "Waist (inches)", "Hip (inches)"];
    const defaultSizes = selectedSizes.length > 0 ? selectedSizes : ["XS", "S", "M", "L", "XL"];
    const defaultData = defaultSizes.map((s) => [s, "", "", ""]);
    emitChange(defaultHeaders, defaultData);
  };

  const hasTable = sizeChartHeaders.length > 0;

  return (
    <div className="space-y-5">
      {/* ── Available Sizes ─────────────────────────────────────────────────── */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          AVAILABLE SIZES
        </label>
        <div className="flex flex-wrap gap-2">
          {SIZES.map((size) => (
            <button
              key={size}
              type="button"
              onClick={() => handleSizeClick(size)}
              disabled={disabled}
              className={`
                min-w-[48px] px-3 py-2 text-sm font-medium border rounded-md transition-all duration-200
                ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-gray-800"}
                ${selectedSizes.includes(size)
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-700 border-gray-300"
                }
              `}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      {/* ── Display Toggle ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-gray-700">
          Show size table on product page:
        </span>
        <button
          type="button"
          onClick={() => onDisplayToggle && onDisplayToggle(!displaySizeTable)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
            displaySizeTable ? "bg-purple-600" : "bg-gray-300"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
              displaySizeTable ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
        <span className={`text-sm font-semibold ${displaySizeTable ? "text-purple-600" : "text-gray-400"}`}>
          {displaySizeTable ? "Displayed" : "Hidden"}
        </span>
      </div>

      {/* ── Size Chart Table Editor ──────────────────────────────────────────── */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-semibold text-gray-700">
            SIZE CHART TABLE
          </label>
          {!hasTable && (
            <button
              type="button"
              onClick={handleInitTable}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Create Table
            </button>
          )}
        </div>

        {!hasTable ? (
          <p className="text-xs text-gray-500 py-2">
            No size chart table yet. Click "Create Table" to build one.
          </p>
        ) : (
          <>
            {/* Scrollable table */}
            <div className="overflow-x-auto rounded border border-gray-200 mb-3">
              <table className="w-full text-sm min-w-max">
                <thead className="bg-gray-200">
                  <tr>
                    {sizeChartHeaders.map((header, cIdx) => (
                      <th key={cIdx} className="p-2 border-b border-r border-gray-300 min-w-[110px]">
                        <div className="flex flex-col items-center gap-1">
                          <input
                            type="text"
                            value={header}
                            onChange={(e) => handleHeaderChange(cIdx, e.target.value)}
                            className="w-full bg-white px-2 py-1 border border-gray-300 rounded text-center text-xs font-bold focus:outline-none focus:border-purple-500"
                          />
                          {sizeChartHeaders.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveColumn(cIdx)}
                              className="text-[10px] text-red-500 hover:text-red-700"
                            >
                              Remove Col
                            </button>
                          )}
                        </div>
                      </th>
                    ))}
                    {/* Delete row column header */}
                    <th className="w-8 bg-gray-200 border-b border-gray-300" />
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {sizeChartData.map((row, rIdx) => (
                    <tr key={rIdx} className="border-b border-gray-200">
                      {row.map((cell, cIdx) => (
                        <td key={cIdx} className="p-1.5 border-r border-gray-200">
                          <input
                            type="text"
                            value={cell}
                            onChange={(e) => handleCellChange(rIdx, cIdx, e.target.value)}
                            className="w-full bg-white px-2 py-1 border border-gray-200 rounded text-center text-xs focus:outline-none focus:border-purple-500"
                          />
                        </td>
                      ))}
                      <td className="p-1.5 text-center">
                        {sizeChartData.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveRow(rIdx)}
                            className="text-red-400 hover:text-red-600"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Table action buttons */}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleAddColumn}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Add Column
              </button>
              <button
                type="button"
                onClick={handleAddRow}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Add Row
              </button>
              <button
                type="button"
                onClick={() => emitChange([], [])}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100 transition-colors ml-auto"
              >
                <Trash2 className="w-3.5 h-3.5" /> Clear Table
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

SizeChartSection.propTypes = {
  selectedSizes: PropTypes.arrayOf(PropTypes.string),
  onSizeChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  displaySizeTable: PropTypes.bool,
  onDisplayToggle: PropTypes.func,
  sizeChartHeaders: PropTypes.arrayOf(PropTypes.string),
  sizeChartData: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
  onSizeChartChange: PropTypes.func,
};

export default SizeChartSection;
